import { formatScore } from '@ctd/core';
import { useState } from 'react';
import { PrintHeader } from '../components/PrintHeader';
import { RoundSelect } from '../components/RoundSelect';
import { PageHeading, PrintButton, TableRegion } from '../components/ui';
import { useSnapshot } from '../context';
import { buildCrosstable } from '../lib/crosstable';
import { useStandings } from './StandingsTab';

export function CrosstableTab() {
  const { tournament: t } = useSnapshot();
  const [chosen, setChosen] = useState<number | null>(null);
  const upTo = chosen !== null && chosen <= t.rounds.length ? chosen : t.rounds.length;
  const { rows: standings, error } = useStandings(upTo);
  const rows = error === null ? buildCrosstable(t, upTo, standings) : [];
  const title = `Crosstable after round ${upTo}`;
  const roundNumbers = Array.from({ length: upTo }, (_, i) => i + 1);

  return (
    <section>
      <div className="page-head no-print">
        <PageHeading>Crosstable</PageHeading>
        <div className="actions">
          {t.rounds.length > 0 && (
            <RoundSelect rounds={t.rounds.length} value={upTo} onValue={setChosen} />
          )}
          <PrintButton label="Print crosstable" />
        </div>
      </div>
      <PrintHeader title={title} />
      {error !== null && (
        <p className="error-box" role="alert">
          The crosstable needs the standings, which cannot be computed: {error}
        </p>
      )}
      {upTo === 0 ? (
        <p className="empty">The crosstable fills in once round 1 is paired.</p>
      ) : (
        <>
          <p className="hint">
            Each cell shows the opponent&rsquo;s place in this table, the colour (w or b) and the
            result.
          </p>
          <TableRegion label={title}>
            <table className="data-table crosstable">
              <caption className="visually-hidden">{title}</caption>
              <thead>
                <tr>
                  <th scope="col" className="num">
                    Rk
                  </th>
                  <th scope="col">Name</th>
                  <th scope="col" className="num">
                    Rating
                  </th>
                  {roundNumbers.map((r) => (
                    <th scope="col" className="num" key={r}>
                      <abbr title={`Round ${r}`}>R{r}</abbr>
                    </th>
                  ))}
                  <th scope="col" className="num">
                    Pts
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.pairingNumber}>
                    <td className="num">{row.rank}</td>
                    <th scope="row">{row.name}</th>
                    <td className="num">{row.rating || '–'}</td>
                    {row.cells.map((c, i) => (
                      <td className="num cross-cell" key={i} title={c.title}>
                        <span aria-hidden="true">{c.text}</span>
                        <span className="visually-hidden">{c.title}</span>
                      </td>
                    ))}
                    <td className="num strong">{formatScore(row.score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableRegion>
        </>
      )}
    </section>
  );
}
